// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';

import { showPageProgress, hidePageProgress, hidePaymentPending } from '@/redux/slices/gui.slice';
import { payCartClear, setBrowserInfos, setFingerprint, updateCartAttribute } from '@/redux/slices/cart.slice';
import { changeStep } from '@/redux/slices/checkout.slice';
import { addToast } from '@/redux/slices/toast.slice';
import { useFeature } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux';
import { LoggerService } from '@/services';
import { useServices } from '@/services/useServices';
import { CallToActionKind } from '@/types';
import { getApiErrorMessage, getErrorMessageByKey } from '@/utils/millesimaUtils';
import { redirectToCheckout } from '@/utils/redirect';

import PayModeItem from '@/components/checkout/payMode/PayModeItem';
import AdyenNewCard from '@/components/checkout/payMode/AdyenNewCard';
import HipayNewCard from '@/components/checkout/payMode/HipayNewCard';
import HipaySavedCards from '@/components/checkout/payMode/HipaySavedCards';
import HipayFingerprint from '@/components/checkout/payMode/HipayFingerprint';
import PayjunctionCsrTransactionId from '@/components/checkout/payMode/PayjunctionCsrTransactionId';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import PayjunctionForm from '@/components/checkout/payMode/PayjunctionForm';
import { STEPS, PAYMENT_STATES, PAYMENT_ERRORS, PAYMENT_PLATFORMS } from '@/constants/checkout';
import { LOG_URL, LOG_MSG } from '@/constants/logger';
import { CS_CUSTOM_ERROR } from '@/constants/datalayer';
import { FEATURES } from '@/constants/features';

export const CreditCardPayMode = ({ updateCardInfo, paymentMode, isDisplayableItem }) => {
  const dispatch = useAppDispatch();

  const userId = useAppSelector(({ user }) => user.content && user.content._id || '');
  const cartId = useAppSelector(({ cart }) => cart.content.id);
  const cart = useAppSelector(({ cart }) => cart);
  const cartPayStatus = useAppSelector(({ cart }) => cart.status.pay);
  const { storeConf } = useAppSelector(({ root }) => root);

  const [showNewCardForm, setShowNewCardForm] = useState(true);
  const [savedCreditCards, setSavedCreditCards] = useState();
  const { userService, datalayerService } = useServices();
  const isFeatureAddCardsEnable = useFeature(storeConf, FEATURES.MODULE_ADD_CARDS);
  const { locale, storeId } = storeConf;

  const { t } = useTranslation();

  const logMessage = useCallback((message, obj) => {
    LoggerService.sendLog(cartId, message, storeId, LOG_URL.PAY, userId, obj);
  }, [cartId, storeId, userId]);

  // useEffect(() => {
  //   if (paymentMode?.action === PAYMENT_PLATFORMS.HIPAY) {
  //     const subscription = loadUserSavedCards();
  //     return () => {
  //       subscription.unsubscribe();
  //     };
  //   }
  // }, [paymentMode?.action]);

  useEffect(() => {
    if (cartPayStatus) {
      const { error, success } = cartPayStatus;
      if (error) {
        processError(error);
      } else if (success) {
        processSuccess(success);
      }
      dispatch(payCartClear());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartPayStatus, paymentMode?.action]);

  /**
   * Switch between "Saved credit cards" and "New credit card"
   */
  const switchButton = () => {
    setShowNewCardForm(!showNewCardForm);
  };

  const processError = (error) => {
    logMessage(LOG_MSG.PAYMENT.ERROR, error);
    if (error.errorCode === PAYMENT_ERRORS.SHIPPING_ERR) {
      redirectToCheckout(getStepWithParams(STEPS.SHIPPING_ADDRESS, PAYMENT_ERRORS.SHIPPING_ERR));
    } else if (error.errorCode === PAYMENT_ERRORS.BILLING_ERR) {
      redirectToCheckout(getStepWithParams(STEPS.BILLING_ADDRESS, PAYMENT_ERRORS.BILLING_ERR));
    } else if (error.errorCode === PAYMENT_ERRORS.DELAYED_DELIVERY_ERROR) {
      dispatch(addToast({ msg: getApiErrorMessage(t, error) }));
    } else {
      dispatch(addToast({ msg: getErrorMessageByKey(t, `errors.genericErrorMessage.${error.status}`, 'errors.payment.generic') }));
      datalayerService.pushCSCustomError(CS_CUSTOM_ERROR.HIPAY, error);
    }
  };

  const processSuccess = (success) => {
    switch (success.state) {
      case PAYMENT_STATES.DECLINED:
      case PAYMENT_STATES.ERROR:
        const ccPayMode = paymentMode?.action.toLowerCase();
        dispatch(hidePaymentPending());
        dispatch(hidePageProgress());
        logMessage(LOG_MSG.PAYMENT.DECLINED, success);
        dispatch(addToast({ msg: getErrorMessageByKey(t, `errors.${ccPayMode}.${success?.reason?.code}`, 'errors.payment.generic') }));
        dispatch(updateCartAttribute({ attributeName: 'paiementMode', newValue: undefined }));
        break;

      case PAYMENT_STATES.COMPLETED:
      case PAYMENT_STATES.PENDING:
        logMessage(LOG_MSG.PAYMENT.SUCCESS, success);
        dispatch(changeStep({ next: STEPS.REPORT }));
        break;

      case PAYMENT_STATES.FORWARDING:
        logMessage(LOG_MSG.PAYMENT.FORWARDING, success);

        if (success?.forwardUrl) {
          // Do redirection => quit application right now
          window.location.href = success.forwardUrl;
        }

        break;

      default:
        logMessage(LOG_MSG.PAYMENT.DECLINED, success);
        dispatch(addToast({ msg: getErrorMessageByKey(t, 'errors.payment.generic') }));
        break;
    }
  };

  const loadUserSavedCards = () => {
    dispatch(showPageProgress());
    // The backend expects an array of payment modes instead of a single one
    if (paymentMode?.action === PAYMENT_PLATFORMS.HIPAY) return;
    return userService.getCreditCards([paymentMode]).subscribe((creditCards) => {
      if (creditCards?.length > 0 && isFeatureAddCardsEnable) {
        setSavedCreditCards(creditCards.filter((cc) => cc.token).map((cc) => ({
          ...cc,
          checked: cc.isDefault
        })));
      } else {
        setShowNewCardForm(true);
        setSavedCreditCards(undefined);
      }
    }, undefined, () => {
      dispatch(hidePageProgress());
    });
  };

  const handleSetFingerprint = (fingerprint) => {
    dispatch(setFingerprint({ fingerprint }));
  };

  const handleSetBrowserInfos = (browserInfo) => {
    dispatch(setBrowserInfos({ browserInfo }));
  };

  return (
    <div className="credit-card tu-credit-card" data-test-id="credit-card-pay-mode">
      {(paymentMode?.action === PAYMENT_PLATFORMS.HIPAY) ? (
        <>
          <HipayFingerprint t={t} setFingerprint={handleSetFingerprint} />
          <div className="credit-card-frame">
            <div className={showNewCardForm ? '' : 'd-none'} data-test-id="credit-card-hipay-newcard-container">
              <HipayNewCard
                t={t}
                locale={locale}
                paymentMode={paymentMode}
                updateCardInfo={updateCardInfo}
                isActive={showNewCardForm}
                setBrowserInfos={handleSetBrowserInfos}
                setFingerprint={null}
              />
              {savedCreditCards && (
                <>
                  <h4 className="saved-credit-cards">
                    {t('checkout:payMode.yourPaymentCards')}
                  </h4>
                  <CallToActionContainer
                    id="btn-use-saved-card"
                    kind={CallToActionKind.SECONDARY}
                    onClick={switchButton}
                  >
                    {t('checkout:payMode.useSavedCreditCard')}
                  </CallToActionContainer>
                </>
              )}
            </div>
          </div>
          {savedCreditCards && (
            <div className={showNewCardForm ? 'd-none' : ''} data-test-id="credit-card-hipay-savecard-container" data-cs-mask>
              <HipaySavedCards
                t={t}
                creditCards={savedCreditCards}
                updateCardInfo={updateCardInfo}
                isActive={!showNewCardForm}
              />
              <h4 className="other-payment-card-title">
                {t('checkout:payMode.otherPaymentCards')}
              </h4>
              <CallToActionContainer
                id="btn-use-credit-card"
                kind={CallToActionKind.SECONDARY}
                onClick={switchButton}
              >
                {t('checkout:payMode.useCreditCard')}
              </CallToActionContainer>
            </div>
          )}
        </>
      ) : ([
        PAYMENT_PLATFORMS.ADYEN,
        PAYMENT_PLATFORMS.ADYEN_APPLEPAY,
        PAYMENT_PLATFORMS.ADYEN_TWINT
      ].includes(paymentMode?.action)) ? (
          <AdyenNewCard t={t} paymentMode={paymentMode} handleResponse={processSuccess}/>
        ) : (paymentMode?.action === PAYMENT_PLATFORMS.CSR_PAY_JUNCTION) ? (
          <PayjunctionCsrTransactionId t={t} />
        ) : (paymentMode?.action === PAYMENT_PLATFORMS.PAY_JUNCTION) ? (
          <PayjunctionForm />
        ) : (
          paymentMode?.action === PAYMENT_PLATFORMS.NO_VALIDATION ||
          paymentMode?.action === PAYMENT_PLATFORMS.CART_ZERO) && isDisplayableItem(paymentMode?._id) ? (
            <PayModeItem item={paymentMode} cart={cart} />
          ) : (
            <></>
          )}
    </div>
  );
};

CreditCardPayMode.propTypes = {
  updateCardInfo: PropTypes.func.isRequired,
  paymentMode: PropTypes.object.isRequired,
  isDisplayableItem: PropTypes.func.isRequired,
};

export default CreditCardPayMode;
